import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { INITIAL_FORM, applyEdit, deriveScenario, normalize } from '../utils/rules.js';
import { interpretPollyResponse, runQuickPricePolly } from '../api/pollyPricing.js';
import { buildPollyPricingPayload } from '../utils/pollyPayload.js';
import { CHANNEL } from '../../../utils/channelUtils.js';

/** The original settles for 2s after a change before re-pricing. */
const DEBOUNCE_MS = 800;

/**
 * Owns the scenario, keeps it legal after every edit, and re-prices whenever it
 * settles. Only the newest request is allowed to write a result.
 */
export function useQuickPrice(isCorrespondent = false) {
    const [form, setForm] = useState(() =>
        normalize({ ...INITIAL_FORM, channel: isCorrespondent ? CHANNEL.CORRESPONDENT : CHANNEL.WHOLESALE }),
    );
    const [resultPolly, setResultPolly] = useState(null);
    const [isPollyPricing, setIsPollyPricing] = useState(true);

    const requestRef = useRef(0);
    const abortRef = useRef(null);

    const scenario = useMemo(() => deriveScenario(form), [form]);

    const update = useCallback((field, value) => {
        setForm((current) => applyEdit(current, field, value));
    }, []);

    useEffect(() => {
        setIsPollyPricing(true);
        const timer = setTimeout(async () => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            const requestId = Date.now();
            requestRef.current = requestId;

            try {
                const bodyPollyRequest = buildPollyPricingPayload({ form, scenario });
                console.log('Polly Request: ', bodyPollyRequest);
                // console.log('Polly Request string: ', JSON.stringify(bodyPollyRequest));
                const responsePolly = await runQuickPricePolly(bodyPollyRequest, controller.signal);
                const finalResult = interpretPollyResponse(scenario, responsePolly, isCorrespondent);
                // console.log('Polly Response: ', responsePolly);
                // console.log('Polly Response string: ', JSON.stringify(responsePolly));

                // if (requestRef.current !== requestId) return;
                setResultPolly(finalResult);
                setIsPollyPricing(false);
            } catch (err) {
                if (err.name === 'AbortError' || requestRef.current !== requestId) return;
                setResultPolly({ status: 'error', title: 'An error occurred when pricing', message: err.message });
                setIsPollyPricing(false);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
        // `scenario` is derived from `form`, so `form` alone is the real dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);

    return { form, scenario, resultPolly, isPollyPricing, update };
}
